import {
  DEFAULT_MAP_ID,
  getMap,
} from '@signalclash/shared';
import Phaser from 'phaser';

import type { ClientApp } from '../game/ClientApp.js';
import { ClientLoop } from '../game/ClientLoop.js';
import { InputRouter } from '../input/InputRouter.js';
import { BackgroundLayer } from '../rendering/BackgroundLayer.js';
import { CameraShake } from '../rendering/CameraShake.js';
import { EffectsLayer } from '../rendering/EffectsLayer.js';
import { FloatingTextLayer } from '../rendering/FloatingTextLayer.js';
import { ObjectivePulseLayer } from '../rendering/ObjectivePulseLayer.js';
import { Renderer } from '../rendering/Renderer.js';
import { WeatherLayer } from '../rendering/WeatherLayer.js';
import { RelayTooltip } from '../ui/RelayTooltip.js';

export class GameScene extends Phaser.Scene {
  private loop: ClientLoop | null = null;
  private input2: InputRouter | null = null;
  private background: BackgroundLayer | null = null;
  private weather: WeatherLayer | null = null;
  private effects: EffectsLayer | null = null;
  private floatingText: FloatingTextLayer | null = null;
  private relayTooltip: RelayTooltip | null = null;
  private objectivePulse: ObjectivePulseLayer | null = null;
  // World size captured at create() so the resize handler can recompute
  // the auto-fit zoom without re-reading the map definition.
  private worldW = 0;
  private worldH = 0;
  // The last auto-fit zoom this scene applied. We compare the camera's
  // current zoom against this value on resize: if they match (within
  // float epsilon), the user hasn't touched the wheel/pinch, so we
  // re-apply the new auto-fit. If they differ, the user has manually
  // zoomed and we leave their zoom alone. This is what makes the
  // CrazyGames fullscreen button "just work" — the iframe viewport
  // grows, we detect that the camera was still at the auto-fit value,
  // and we re-fit the world to the new viewport instead of leaving a
  // sea of black void around the smaller pre-fullscreen frame.
  private lastAutoZoom: number | null = null;
  private resizeHandler: (() => void) | null = null;

  constructor() {
    super('Game');
  }

  // Compute the zoom value that lets the current viewport show the full
  // world without empty borders. Same formula the initial create() used
  // (factored out so the resize handler can reuse it verbatim).
  //   - zoomToFit:  largest zoom that keeps the entire world visible
  //   - zoomToFill: smallest zoom that has no black bars on either axis
  //   - target:     the larger of `zoomToFill` and `zoomToFit * 1.6` so
  //                 wide screens fill cleanly while near-square screens
  //                 still get a tight tactical framing.
  //   - clamped to [0.5, 1.5] so weird viewports don't produce zooms
  //                 that read as "where am I" levels of close/far.
  private computeAutoZoom(vw: number, vh: number): number {
    if (this.worldW <= 0 || this.worldH <= 0) return 1;
    const zoomToFit = Math.min(vw / this.worldW, vh / this.worldH);
    const zoomToFill = Math.max(vw / this.worldW, vh / this.worldH);
    const target = Math.max(zoomToFill, zoomToFit * 1.6);
    return Math.max(0.5, Math.min(1.5, target));
  }

  create(): void {
    const app = this.registry.get('app') as ClientApp;
    // V1.5+ — camera bounds are PER-MAP, not pinned to Foundry's 2000×2000.
    // Crucible is 3000×2400; reading from the loaded map keeps the renderer
    // honest about the actual world size.
    const mapId = app.getCurrentMapId() ?? DEFAULT_MAP_ID;
    const mapDef = getMap(mapId);
    const worldW = mapDef.width;
    const worldH = mapDef.height;
    this.worldW = worldW;
    this.worldH = worldH;
    this.cameras.main.setBounds(0, 0, worldW, worldH);
    const vw = typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : worldW;
    const vh = typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : worldH;
    // Zoom: at minimum use the "fill" ratio (max of the two axes) so the
    // viewport never exceeds the world width on landscape screens — that
    // eliminates the dark/black side bars that appeared on 2560×1440 and
    // other wide viewports.  The 1.6× "fit" multiplier is kept as a second
    // candidate so portrait / near-square screens still get a usefully
    // close starting view.  Max raised to 1.5 to allow tighter framing on
    // ultrawide monitors without being clipped by the old 1.2 ceiling.
    const initialZoom = this.computeAutoZoom(vw, vh);
    this.cameras.main.setZoom(initialZoom);
    this.lastAutoZoom = initialZoom;
    // Wire viewport-resize handling. Phaser's RESIZE event fires for
    // every viewport change including fullscreen enter/exit and browser
    // window drag-resize. We re-apply the auto-fit zoom only when the
    // user hasn't manually wheeled/pinched away from it — see
    // lastAutoZoom doc comment above.
    this.resizeHandler = (): void => this.onViewportResize();
    this.scale.on(Phaser.Scale.Events.RESIZE, this.resizeHandler);
    // Anchor the opening view on the local player's own base rather than
    // the geometric map center.  A 35 % lerp toward the map center also
    // shows the approach lane between the Core and the mid area, giving an
    // immediately useful tactical framing without revealing the entire map.
    // Falls back to the map center for spectators or unknown slots.
    const viewerId   = app.getAssignedPlayerId();
    const playerCore = mapDef.cores.find(c => c.ownerId === viewerId);
    const TOWARD_CENTER = 0.35;
    const camX = playerCore
      ? playerCore.pos.x + (worldW / 2 - playerCore.pos.x) * TOWARD_CENTER
      : worldW / 2;
    const camY = playerCore
      ? playerCore.pos.y + (worldH / 2 - playerCore.pos.y) * TOWARD_CENTER
      : worldH / 2;
    this.cameras.main.centerOn(camX, camY);
    // Subtle dark base behind the tile layer — keeps borders dark when the
    // camera overshoots the world at low zoom on wide viewports.
    this.cameras.main.setBackgroundColor('#070a0e');
    this.input.mouse?.disableContextMenu();

    this.background = new BackgroundLayer(this, mapDef);
    this.weather = new WeatherLayer(this, mapDef);

    const renderer = new Renderer(this, app.humanConnection, app.selection, {
      buildMode: app.buildMode,
      // Network mode: ClientApp updated `assignedPlayerId` from the
      // server's `assigned` Colyseus message before this scene
      // started. Local mode: stays PLAYER_ZERO (the constructor
      // default). Reading from the app rather than hardcoding means a
      // slot-1 client renders the world from its own perspective.
      viewerId: app.getAssignedPlayerId(),
      getInput: () => this.input2,
    });
    this.input2 = new InputRouter({
      scene: this,
      app,
      selection: app.selection,
      human: app.human,
      buildMode: app.buildMode,
      settings: app.settings,
      getZoomMin: () => {
        const vw = typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : this.worldW;
        const vh = typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : this.worldH;
        if (this.worldW <= 0 || this.worldH <= 0) return 0.5;
        return Math.max(vw / this.worldW, vh / this.worldH);
      },
    });
    // Publish the router so MobileActionBar / ArmedBanner can drive it from
    // their on-screen buttons. Cleared in SHUTDOWN below so a stale router
    // doesn't survive scene transitions.
    app.setInputRouter(this.input2);
    this.loop = new ClientLoop(app.host, renderer, app.debug, app.perfOverlay);

    // V1.7 cinematic FX — particle-driven combat effects + camera shake.
    // EffectsLayer subscribes to humanConnection.onSnapshot exactly like
    // Sfx does (Rule 1: presentational, never writes back to MatchState).
    // CameraShake is injected so EffectsLayer stays pure rendering.
    const shake = new CameraShake(this);
    this.effects = new EffectsLayer(this, app.getAssignedPlayerId(), shake);
    this.effects.attach(app.humanConnection);

    // V4 release-readiness pass — floating world-space text (+SIGNAL on
    // capture, CORE KO on Core destruction) and the desktop hover tooltip
    // for relays. Both are presentation-only (Rule 1).
    this.floatingText = new FloatingTextLayer(this, app.getAssignedPlayerId());
    this.floatingText.attach(app.humanConnection);
    this.relayTooltip = new RelayTooltip(app.getAssignedPlayerId());
    // V1.9 — objective pulse layer (yellow ring on the first neutral
    // relay during the tutorial capture step). Owned by the scene so
    // the Phaser Graphics is cleaned up on shutdown; the tutorial drives
    // it via the callback wired in ClientApp.
    this.objectivePulse = new ObjectivePulseLayer(this);
    app.registerObjectivePulse(this.objectivePulse);
    // Register the loop with the app so the PauseController can pause it.
    app.registerClientLoop(this.loop);

    // Click-to-jump on the minimap centers this scene's camera. Bound for the
    // lifetime of the scene; unbound on shutdown so a stale callback can't
    // address a destroyed Phaser camera after a scene transition.
    app.minimap.bindCamera((wx, wy) => this.cameras.main.centerOn(wx, wy));

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      // Each dispose step is independently guarded so a single throw
      // (e.g. renderer.dispose after the connection already tore down,
      // weather.dispose on a lost-context canvas) cannot skip the
      // remaining cleanups. Skipped disposes leak GameObjects and
      // event subscribers, accumulating across rematches until a
      // long session burns enough memory to OOM the tab — a slow-
      // burn crash signature on the portal's session-length metric.
      const safe = (label: string, fn: () => void): void => {
        try {
          fn();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[shutdown] ${label} dispose threw`, err);
        }
      };
      safe('input', () => this.input2?.dispose());
      this.input2 = null;
      safe('setInputRouter', () => app.setInputRouter(null));
      safe('renderer', () => renderer.dispose());
      safe('minimap.unbindCamera', () => app.minimap.unbindCamera());
      safe('background', () => this.background?.dispose());
      this.background = null;
      safe('weather', () => this.weather?.dispose());
      this.weather = null;
      safe('effects', () => this.effects?.dispose());
      this.effects = null;
      safe('floatingText', () => this.floatingText?.dispose());
      this.floatingText = null;
      safe('relayTooltip', () => this.relayTooltip?.dispose());
      this.relayTooltip = null;
      safe('objectivePulse', () => this.objectivePulse?.dispose());
      this.objectivePulse = null;
      safe('registerObjectivePulse(null)', () => app.registerObjectivePulse(null));
      safe('registerClientLoop(null)', () => app.registerClientLoop(null));
      // Detach the viewport-resize listener so a stale callback can't
      // fire against a destroyed camera after the scene has shut down
      // (rematch path: GameScene rebuilds; the Scale event manager
      // outlives the scene).
      if (this.resizeHandler !== null) {
        safe('scale.off(RESIZE)', () =>
          this.scale.off(Phaser.Scale.Events.RESIZE, this.resizeHandler!),
        );
        this.resizeHandler = null;
      }
      this.lastAutoZoom = null;
    });
  }

  // Viewport-size change handler. Fires on browser resize, fullscreen
  // enter/exit, and (on the CrazyGames portal) when the player toggles
  // the portal's own fullscreen button — that resize is what made the
  // world look "zoomed out": the iframe viewport grew, but the camera
  // still had the zoom computed for the smaller pre-fullscreen size,
  // so the same world rendered into a much larger canvas with empty
  // black around the world bounds.
  //
  // We only auto-adjust when the user hasn't manually zoomed since the
  // last auto-fit. Manual zoom (wheel / pinch) is sticky on purpose
  // (Rule: "zoom in/out as we please") — clobbering it on every
  // resize would be obnoxious. The float comparison against
  // lastAutoZoom is exact because both values flow through the same
  // Math.max/min clamp; epsilon tolerance is added defensively in case
  // a future Phaser version rounds the zoom internally.
  private onViewportResize(): void {
    if (this.lastAutoZoom === null) return;
    const cam = this.cameras.main;
    const currentZoom = cam.zoom;
    const ZOOM_EPSILON = 1e-4;
    if (Math.abs(currentZoom - this.lastAutoZoom) > ZOOM_EPSILON) {
      // User has wheeled or pinched — leave their zoom alone.
      return;
    }
    const vw = typeof window !== 'undefined' && window.innerWidth > 0
      ? window.innerWidth
      : this.worldW;
    const vh = typeof window !== 'undefined' && window.innerHeight > 0
      ? window.innerHeight
      : this.worldH;
    const next = this.computeAutoZoom(vw, vh);
    cam.setZoom(next);
    this.lastAutoZoom = next;
  }

  override update(time: number): void {
    // Per-frame containment: a single throw here would otherwise repeat
    // every frame (Phaser's update pump re-enters this method ~60 Hz),
    // saturating the console and freezing the visible game. Each
    // sub-update is wrapped so a regression in one layer (e.g. weather
    // pool corruption, FX texture lost on iOS) can't take down the
    // simulation or the input router.
    try {
      this.loop?.tick(time);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[game] loop.tick threw', err);
    }
    try {
      this.background?.update(time);
      this.weather?.update(time);
      this.effects?.update(time);
      this.floatingText?.update(time);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[game] layer update threw', err);
    }
    try {
      const cursor = this.input2?.getWorldCursor();
      if (cursor !== undefined) {
        const app = this.registry.get('app') as ClientApp;
        app.debug.setCursor(cursor.x, cursor.y);
        // Keep the ArmedBanner snapped to current arming state per-frame so
        // keyboard-armed actions (Q/E/A/B) flip the banner immediately on
        // desktop-with-touchscreen even between snapshots.
        app.armedBanner.refresh();
        // Drive the relay hover tooltip on each frame. RelayTooltip itself
        // bails out for touch profiles, so this is a cheap no-op on phones.
        // Cursor (world coords) drives relay picking; activePointer.x/y is
        // the canvas-relative screen position used to anchor the DOM tip.
        const snap = app.getLatestSnapshot();
        const pointer = this.input.activePointer;
        const canvas = this.game.canvas;
        const rect = canvas?.getBoundingClientRect();
        const screenX = (rect?.left ?? 0) + pointer.x;
        const screenY = (rect?.top ?? 0) + pointer.y;
        this.relayTooltip?.update(snap, cursor.x, cursor.y, screenX, screenY);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[game] cursor/tooltip update threw', err);
    }
  }
}
